import { useState } from 'react';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import {
  Modal,
  ModalFooter,
  ModalBody,
  ModalHeader,
  ModalBackButton,
  MODAL_SIZES,
  MODAL_VARIANTS,
} from 'ui/modal';
import { ContentSwap } from 'ui-beta/contentSwap';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import { FolderLocation } from '../../folders/folderLocation';
import { TreeView, TreeViewFolderData, TreeViewQueryResponse } from '../../folders/treeView';

export const displayContent = {
  back: 'Back',
  cancel: 'Cancel',
  folderLocationLabel: 'Folder location',
  modalHeader: 'New folder',
  newNameLabel: 'Folder name',
  newNamePlaceholder: `Example: "Spring 2021 Sale"`,
  next: 'Create folder',
  searchForFolder: 'Search for folder',
  selectLocationText: 'Select location',
  selectDifferentLocationText: 'Select different location',
  selectRootText: 'Select overview',
  invalidFolderName: 'Folder name is not valid',
};

enum NEW_FOLDER_VIEWS {
  Default = 'default',
  SelectLocation = 'select-location',
}

type NewFolderModalProps = {
  defaultDestinationFolderId: FolderId;
  onClose: () => void;
  onConfirm: (
    newFolderName: string,
    newFolderDestination: AncestorNameData & AncestorIdData,
  ) => void;
  rootFolderId: FolderId;
  treeViewQuery: (id: FolderId) => TreeViewQueryResponse;
  maxNameLength?: number;
};

export const NewFolderModal = ({
  onClose,
  onConfirm,
  rootFolderId,
  defaultDestinationFolderId,
  treeViewQuery,
  maxNameLength,
}: NewFolderModalProps) => {
  const [folderName, setFolderName] = useState('');
  const [activeView, setActiveView] = useState<NEW_FOLDER_VIEWS>(NEW_FOLDER_VIEWS.Default);

  const [selectedFolderId, setSelectedFolderId] = useState<FolderId>(defaultDestinationFolderId);
  const [proposedFolderId, setProposedFolderId] = useState<FolderId | null>(null);

  // get tree view data for the selected folder id (if one is specified, or default to the root)
  const { folderData, isLoading } = treeViewQuery(selectedFolderId);

  const { ancestors = [], id: destinationFolderId, name: destinationFolderName } = folderData;

  const pathInfo = [...ancestors, { id: destinationFolderId, name: destinationFolderName }];

  const handleConfirm = () => {
    if (selectedFolderId) {
      onConfirm(folderName, { id: destinationFolderId, name: destinationFolderName });
    }
  };

  return (
    <Modal
      data-test="new-folder-modal-wrapper"
      id="new-folder-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ContentSwap activeView={activeView}>
        <div id={NEW_FOLDER_VIEWS.Default}>
          <ModalHeader data-test="new-folder-modal-header">
            {displayContent.modalHeader}
          </ModalHeader>
          <ModalBody data-test="new-folder-modal-body" id="new-folder-modal-body">
            <FormField
              errorText={displayContent.invalidFolderName}
              key="new-folder-name"
              hasError={false}
              inputId="new-folder-name"
              labelText={displayContent.newNameLabel}
              restrictionText={maxNameLength ? `${folderName.length}/${maxNameLength}` : undefined}
            >
              <InputText
                key="field-new-folder-name"
                id="new-folder-name"
                onChange={(nameOfFolder: string) => {
                  setFolderName(nameOfFolder);
                }}
                placeholder={displayContent.newNamePlaceholder}
                type="text"
                value={folderName}
                maxLength={maxNameLength}
              />
            </FormField>
            <FormField
              errorText=""
              hasError={false}
              inputId="folder-location-selector"
              key="folder-location-select"
              labelText={displayContent.folderLocationLabel}
            >
              <FolderLocation
                folderAncestors={pathInfo || []}
                id="folder-location-path"
                onChange={setProposedFolderId}
              />

              <SecondaryButton
                id="folder-location-select-btn"
                key="field-location-selector"
                onClick={() => {
                  setActiveView(NEW_FOLDER_VIEWS.SelectLocation);
                }}
              >
                {displayContent.selectDifferentLocationText}
              </SecondaryButton>
            </FormField>
          </ModalBody>

          <ModalFooter
            cancelLabel={displayContent.cancel}
            data-test="new-folder-modal-actions"
            id="new-folder-modal-actions"
          >
            <PrimaryButton
              onClick={handleConfirm}
              isDisabled={!folderName}
              size={BUTTON_SIZES.LARGE}
            >
              {displayContent.next}
            </PrimaryButton>
          </ModalFooter>
        </div>
        <div id={NEW_FOLDER_VIEWS.SelectLocation}>
          <ModalHeader data-test="new-folder-modal-header">
            <ModalBackButton
              onClick={() => {
                setActiveView(NEW_FOLDER_VIEWS.Default);
              }}
            >
              {displayContent.back}
            </ModalBackButton>
          </ModalHeader>
          <ModalBody data-test="new-folder-modal-body" id="new-folder-modal-body">
            <TreeView
              isLoading={isLoading}
              defaultSelectedAncestors={pathInfo}
              defaultSelectedFolderName={destinationFolderName}
              defaultSelectedFolder={selectedFolderId || defaultDestinationFolderId}
              handleChange={(folderData: TreeViewFolderData) => setProposedFolderId(folderData.id)}
              key="body-tree-view-container"
              query={treeViewQuery}
              searchText={''}
              rootFolderId={rootFolderId}
            />
          </ModalBody>
          <ModalFooter
            cancelLabel={displayContent.cancel}
            data-test="new-folder-modal-actions"
            id="new-folder-modal-actions"
          >
            <SecondaryButton
              data-test="select-root-button"
              onClick={() => {
                setSelectedFolderId(rootFolderId);
                setProposedFolderId(null);
                setActiveView(NEW_FOLDER_VIEWS.Default);
              }}
              size={BUTTON_SIZES.LARGE}
            >
              {displayContent.selectRootText}
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setSelectedFolderId(proposedFolderId || defaultDestinationFolderId);
                setProposedFolderId(null);
                setActiveView(NEW_FOLDER_VIEWS.Default);
              }}
              isDisabled={!proposedFolderId}
              size={BUTTON_SIZES.LARGE}
            >
              {displayContent.selectLocationText}
            </PrimaryButton>
          </ModalFooter>
        </div>
      </ContentSwap>
    </Modal>
  );
};
